import { Col, Row, Spin } from 'antd'
import Avatar from 'components/Avatar'
import CustomTag from 'components/CustomTag'
import FileReader from 'components/Uploader/FileReader'
import FileReaderVisibleOnly from 'components/Uploader/FileReaderVisibleOnly'
import ApartmentIcon from 'components/icons/ApartmentIcon'
import AwardsIconWork from 'components/icons/AwardsIconWork'
import {
  EditorContent,
  GreyText,
  Marks,
  RewardTite,
  SecondaryHeading,
  StyledP,
  SubFLex,
} from 'modules/Resume/Style/style'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { COLORS } from 'theme'
import { ExclamationCircleFilled } from '@ant-design/icons'
import styled from 'styled-components'

const PendingIcon = styled(ExclamationCircleFilled)`
  color: #ff4d4f !important;
  fill: #ff4d4f !important;
  font-size: 16px;
  margin-left: 8px;
`

const EducationSection = ({ studentDetails, instituteName, pendingData, frozenFields }) => {
  const [educationList, setEducationList] = useState([])
  const currentDate = moment()
  const [loading, setLoading] = useState(true)
  
  const hasFieldPending = (item, fieldName) => {
    return item?._pendingFields && item._pendingFields[fieldName] !== undefined
  }
  useEffect(() => {
    setLoading(true)
    let currentCourse = {
      id: studentDetails?.currentCourse?.id,
      isCurrentCourse: true,
      educationLevel: studentDetails?.currentCourse?.educationLevel,
      institutionName: instituteName?.campusName,
      board: '',
      degree: studentDetails?.currentCourse?.degree,
      department: studentDetails?.currentCourse?.department,
      specialisation: studentDetails?.currentCourse?.specialisation,
      university: studentDetails?.currentCourse?.university?.university || studentDetails?.currentCourse?.university,
      degreeStreamMapId: studentDetails?.currentCourse?.degreeStreamMapId,
      startedOn: studentDetails?.currentCourse?.startedOn,
      endedOn: studentDetails?.currentCourse?.endedOn,
      marks: studentDetails?.currentCourse?.marks,
      averageMarks: studentDetails?.currentCourse?.averageMarks,
      skills: studentDetails?.currentCourse?.skills,
      state: studentDetails?.currentCourse?.state || '',
      city: studentDetails?.currentCourse?.city || '',
      country: studentDetails?.currentCourse?.country || '',
      markSheetUrl: studentDetails?.currentCourse?.markSheetUrl,
    }
    
    // Merge pending currentCourse data if exists
    const pendingCurrentCourse = pendingData?.currentCourse
    if (pendingCurrentCourse) {
      if (pendingCurrentCourse._pendingFields) {
        // Handle structured format with _pendingFields
        Object.keys(pendingCurrentCourse._pendingFields).forEach(fieldName => {
          const pendingValue = pendingCurrentCourse._pendingFields[fieldName].value
          if (pendingValue !== null && pendingValue !== undefined) {
            currentCourse[fieldName] = pendingValue
          }
        })
        currentCourse._hasPendingChanges = true
        currentCourse._pendingFields = pendingCurrentCourse._pendingFields
        currentCourse._requestId = Object.values(pendingCurrentCourse._pendingFields)[0]?._requestId
      } else {
        // Handle direct format where pending data is on the object itself
        const pendingFieldsMap = {}
        Object.keys(pendingCurrentCourse).forEach(fieldName => {
          // GET /students/:id/pending-data always returns a currentCourse scaffold of
          // six explicit nulls (averageMarks, noOfArrears, historyOfArrears,
          // university, specialisation, marks) whether or not anything is actually
          // awaiting approval. Accepting null here minted a pending entry for each of
          // them — and a 'pending' _requestId — so every student saw the verification
          // icon on those fields. A null is the absence of a pending change.
          if (fieldName !== 'id' && fieldName !== 'isCurrentCourse' && pendingCurrentCourse[fieldName] !== undefined && pendingCurrentCourse[fieldName] !== null) {
            let fieldValue = pendingCurrentCourse[fieldName]
            
            if (fieldValue !== null && fieldValue !== undefined) {
              // Handle nested specialisation object
              if (fieldName === 'specialisation' && typeof fieldValue === 'object' && fieldValue !== null) {
                if (fieldValue.specialisation) {
                  currentCourse.specialisation = fieldValue.specialisation
                  if (fieldValue.specialisationId) {
                    currentCourse.specialisationId = fieldValue.specialisationId
                  }
                  pendingFieldsMap.specialisation = { value: fieldValue.specialisation }
                  if (fieldValue.specialisationId) {
                    pendingFieldsMap.specialisationId = { value: fieldValue.specialisationId }
                  }
                }
              } else {
                currentCourse[fieldName] = fieldValue
                pendingFieldsMap[fieldName] = { value: fieldValue }
              }
            }
          }
        })
        if (Object.keys(pendingFieldsMap).length > 0) {
          currentCourse._hasPendingChanges = true
          currentCourse._pendingFields = pendingFieldsMap
          currentCourse._requestId = 'pending'
        }
      }
    }
    
    const approvedEducation = studentDetails?.education || []
    const pendingEducation = pendingData?.education || []
    
    const pendingMap = new Map()
    pendingEducation.forEach(e => {
      if (e.id && !e.id.startsWith('temp-')) {
        pendingMap.set(e.id, e)
      }
    })
    
    const mergedEducation = approvedEducation.map(approvedEdu => {
      const pendingEdu = pendingMap.get(approvedEdu.id)
      
      if (pendingEdu && pendingEdu._pendingFields) {
        const merged = { ...approvedEdu }
        
        Object.keys(pendingEdu._pendingFields).forEach(fieldName => {
          merged[fieldName] = pendingEdu._pendingFields[fieldName].value
        })
        
        merged._hasPendingChanges = true
        merged._pendingFields = pendingEdu._pendingFields
        merged._requestId = Object.values(pendingEdu._pendingFields)[0]?._requestId
        
        return merged
      }
      
      return approvedEdu
    })
    
    const newPendingEducation = pendingEducation.filter(e => 
      e._isNew || (e.id && e.id.startsWith('temp-'))
    )
    
    const allEducation = [...mergedEducation, ...newPendingEducation]
    
    let schoolLevel = []
    let collegeLevel = []
    allEducation?.map((item, index) => {
      if (
        item?.educationLevel === 'twelfth' ||
        item?.educationLevel === 'tenth'
      ) {
        schoolLevel.push(item)
      } else {
        collegeLevel.push(item)
      }
    })
    if (studentDetails?.student?.isExpCandidate) {
      setLoading(true)
      let educationList = [...collegeLevel, ...schoolLevel]

      educationList?.sort((a, b) => {
        const endedA = moment(parseInt(a?.endedOn))
        const endedB = moment(parseInt(b?.endedOn))

        // Check if both dates are valid
        if (!endedA.isValid() || !endedB.isValid()) {
          return 0
        }

        // Sort by the "ended_in" date in descending order
        if (endedB.isAfter(endedA)) return 1
        if (endedA.isAfter(endedB)) return -1
      })
      setEducationList(educationList)
      setLoading(false)
    } else if (!studentDetails?.student?.isExpCandidate) {
      setLoading(true)

      let educationList =
        allEducation?.length > 0
          ? [currentCourse, ...collegeLevel, ...schoolLevel]
          : [currentCourse]

      educationList?.sort((a, b) => {
        const endedA = moment(parseInt(a?.endedOn))
        const endedB = moment(parseInt(b?.endedOn))

        // Check if both dates are valid
        if (!endedA.isValid() || !endedB.isValid()) {
          return 0
        }

        // Sort by the "ended_in" date in descending order
        if (endedB.isAfter(endedA)) return 1
        if (endedA.isAfter(endedB)) return -1
      })
      setEducationList(educationList)

      setLoading(false)
    }
  }, [studentDetails, instituteName, pendingData])
  useEffect(() => {
    let count = 0
    for (let i = 0; i < studentDetails?.currentCourse?.marks?.length; i++) {
      if (studentDetails?.currentCourse?.marks[i].mark === 0) {
        count++
      }
    }
  }, [studentDetails])
  const getEduLevel = value => {
    if (
      value.educationLevel === 'ug' ||
      value.educationLevel === 'pg' ||
      value.educationLevel === 'phd' ||
      value.educationLevel === 'diploma' ||
      value.educationLevel === 'p_g_diploma'
    ) {
      return value.degree?.toLowerCase() + ' in ' + value.department
    } else {
      return value.board
    }
  }
  const getEducationDate = value => {
    if (
      value.educationLevel === 'ug' ||
      value.educationLevel === 'pg' ||
      value.educationLevel === 'phd' ||
      value.educationLevel === 'diploma' ||
      value.educationLevel === 'p_g_diploma'
    ) {
      return (
        <span>
          {value?.startedOn > 0 ? (
            <>{moment(parseInt(value?.startedOn)).format('YYYY')} - </>
          ) : null}
          {moment(parseInt(value?.endedOn)).format('YYYY')}{' '}
        </span>
      )
    } else if (value?.endedOn > 0) {
      return <span>{moment(parseInt(value?.endedOn)).format('YYYY')} </span>
    }
  }

  const roundOff = mark => {
    return Number(mark).toFixed(2)
  }

  const getEducationLevelLabel = level => {
    switch (level) {
      case 'tenth':
        return '10th'
      case 'twelfth':
        return '12th'
      case 'puc':
        return 'Diploma'
      case 'p_g_diploma':
        return 'PG DIPLOMA'
      case 'diploma':
        return 'Diploma'
      case 'ug':
        return 'UG'
      case 'pg':
        return 'PG '
      case 'phd':
        return 'PD'
      case 'pd':
        return 'PD'
      default:
        break
    }
  }

  return (
    <>
      {!loading && (
        <SubFLex column gap="25px" style={{ width: '100%' }}>
          {!loading &&
            educationList?.length > 0 &&
            educationList?.map((item, index) => (
              <Row style={{ width: '100%' }} key={index + 'education'}>
                <Col span={20}>
                  <SubFLex gap="12px">
                    <div>
                      <Avatar
                        background="none"
                        size={48}
                        icon={<ApartmentIcon />}
                        boxShadow={true}
                        padding="5px"
                      />
                    </div>
                    <SubFLex column style={{ width: '90%' }}>
                      <SubFLex gap="20px" flexStart>
                        <SecondaryHeading fontWeight="600" style={{ display: 'flex', alignItems: 'center' }}>
                          <span>
                            {getEducationLevelLabel(item?.educationLevel)}
                            {getEducationLevelLabel(item?.educationLevel) &&
                              item?.institutionName &&
                              ' - '}
                            {item?.institutionName}
                            {hasFieldPending(item, 'institutionName') && <PendingIcon />}
                          </span>
                        </SecondaryHeading>

                        {(item?._requestId || (item?.isCurrentCourse && (hasFieldPending(item, 'markSheetUrl') || hasFieldPending(item, 'degree') || hasFieldPending(item, 'department')))) && (
                          <CustomTag
                            message={item?.id ? "Verification Pending" : "Verification Pending"}
                            bordered={true}
                            background={'#eb5d60'}
                            border={'#eb5d60'}
                            color={'#fff'}
                          />
                        )}

                        {moment(parseInt(item?.endedOn)).format('YYYY-MM-DD') >
                          currentDate.format('YYYY-MM-DD') ===
                        true ? (
                          <CustomTag
                            message="Currently Pursuing"
                            bordered={true}
                            background="none"
                            colorType="grey"
                            border={COLORS.GREY_T_80}
                          />
                        ) : (
                          <></>
                        )}
                      </SubFLex>
                      <GreyText color={COLORS.GREY_T_60} style={{ display: 'flex', alignItems: 'center' }}>
                        <span>
                          {getEducationDate(item)} {item?.city}
                          {item?.city && ','}
                          {item?.state}
                        </span>
                        {(hasFieldPending(item, 'startedOn') || hasFieldPending(item, 'endedOn')) && <PendingIcon />}
                      </GreyText>
                      <GreyText style={{ display: 'flex', alignItems: 'center' }}>
                        <span>
                          {getEduLevel(item)}{' '}
                          {item?.specialisation
                            ? `(${typeof item?.specialisation === 'object' ? item?.specialisation?.specialisation || item?.specialisation?.name || '' : item?.specialisation})`
                            : null}
                        </span>
                        {(hasFieldPending(item, 'degree') || hasFieldPending(item, 'department') || hasFieldPending(item, 'board') || hasFieldPending(item, 'specialisation')) && <PendingIcon />}
                      </GreyText>
                      {/* {item?.university && (
                        <GreyText style={{ display: 'flex', alignItems: 'center' }}>
                          <span>
                            from {typeof item?.university === 'object' ? item?.university?.university : item?.university}
                          </span>
                          {hasFieldPending(item, 'university') && <PendingIcon />}
                        </GreyText>
                      )} */}
                      {item?.skills?.length > 0 && (
                        <SecondaryHeading fontWeight="600">
                          Skills
                        </SecondaryHeading>
                      )}
                      <SubFLex
                        centerVertically
                        gap="5px"
                        marginBottom="15px"
                        wrap
                      >
                        {item?.skills?.map((value, key) => (
                          <CustomTag
                            message={value?.name}
                            key={key + 'skills'}
                            bordered={true}
                            background={'#F2F3F5'}
                            border={'#F2F3F5'}
                          />
                        ))}
                      </SubFLex>
                      {item?.markSheetUrl &&
                        Object.keys(item?.markSheetUrl).length > 0 && (
                          <>
                            {hasFieldPending(item, 'markSheetUrl') ? (
                              <div
                                style={{
                                  background: '#FFE5E6',
                                  padding: '12px 16px',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  marginTop: '8px',
                                  marginBottom: '8px'
                                }}
                              >
                                <span style={{ fontSize: '14px', color: '#1F2937' }}>
                                  Marksheet Submitted - <span style={{ color: '#3249D7', fontWeight: 500 }}>{item?.markSheetUrl?.name || 'Document.jpg'}</span>
                                </span>
                                <button
                                  onClick={() => {
                                    const newWindow = window.open(item?.markSheetUrl?.url, '_blank', 'noopener,noreferrer')
                                    if (newWindow) newWindow.opener = null
                                  }}
                                  style={{
                                    background: '#3249D7',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '6px 20px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                  }}
                                >
                                  View
                                </button>
                              </div>
                            ) : (
                              <FileReaderVisibleOnly 
                                file={item?.markSheetUrl} 
                                disabled={frozenFields?.markSheetUrl || false}
                              />
                            )}
                          </>
                        )}
                      <br></br>
                      {item?.awards?.length > 0 &&
                        studentDetails?.student?.isExpCandidate && (
                          <RewardTite>Rewards and Recognition </RewardTite>
                        )}
                      {item?.awards?.map((value, key) => (
                        <SubFLex
                          column
                          gap="25px"
                          marginBottom="10px"
                          key={key + 'awards'}
                        >
                          <SubFLex gap="12px">
                            <div>
                              <Avatar
                                background="none"
                                size={48}
                                icon={<AwardsIconWork />}
                                boxShadow={true}
                                padding="5px"
                              />
                            </div>
                            <SubFLex column style={{ width: '90%' }}>
                              <SecondaryHeading>
                                {value?.title}
                              </SecondaryHeading>

                              <GreyText color={COLORS.GREY_T_60}>
                                {moment(value?.awarded_on).format(
                                  'DD MMM YYYY'
                                )}
                              </GreyText>

                              <StyledP>
                                {' '}
                                <EditorContent
                                  dangerouslySetInnerHTML={{
                                    __html: value?.description || '',
                                  }}
                                />{' '}
                              </StyledP>
                              {value?.certificate && (
                                <FileReaderVisibleOnly
                                  file={value?.certificate}
                                />
                              )}
                            </SubFLex>
                          </SubFLex>
                        </SubFLex>
                      ))}
                    </SubFLex>
                  </SubFLex>
                </Col>
                <Col span={4}>
                  <div style={{ width: 'max-content' }}>
                      <Marks style={{ display: 'flex', alignItems: 'center' }}>
                        <span>
                          {!item?.isCurrentCourse &&
                            'Marks - ' + roundOff(hasFieldPending(item, 'marks') ? item._pendingFields.marks.value : item?.marks) + '%'}

                          {item?.isCurrentCourse && Number(hasFieldPending(item, 'averageMarks') ? item._pendingFields.averageMarks.value : item?.averageMarks) >= 1
                            ? 'Marks  - ' + `${roundOff(hasFieldPending(item, 'averageMarks') ? item._pendingFields.averageMarks.value : item?.averageMarks)}` + '% '
                            : null}
                        </span>
                        {(hasFieldPending(item, 'marks') || hasFieldPending(item, 'averageMarks')) && <PendingIcon />}
                      </Marks>
                    </div>
                  </Col>
                </Row>
              ))}
        </SubFLex>
      )}
      {loading && <Spin />}
    </>
  )
}

export default EducationSection
